import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Check, Scissors, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  getOperacionesCostura,
  createOperacionCostura,
  updateOperacionCostura,
  deleteOperacionCostura,
  reordenarOperacionesCostura,
} from '../services/api';
import { useEmpresa } from '../context/EmpresaContext';

const fmtMoney = (v) => `S/ ${Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  nombre: '',
  servicio_asociado: 'Costura',
  tarifa_unitaria: '',
  vigente_desde: today(),
  notas: '',
  activo: true,
});

export default function OperacionesCostura() {
  const { empresaActual } = useEmpresa();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incluirInactivas, setIncluirInactivas] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  // Drag & drop
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const reorderTimer = useRef(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getOperacionesCostura({ incluir_inactivas: incluirInactivas });
      setItems(r.data || []);
    } catch (e) {
      toast.error('Error al cargar operaciones');
    } finally {
      setLoading(false);
    }
  }, [incluirInactivas]);

  useEffect(() => {
    if (empresaActual) cargar();
  }, [empresaActual, cargar]);

  const startEdit = (op) => {
    setEditingId(op.id);
    setForm({
      nombre: op.nombre || '',
      servicio_asociado: op.servicio_asociado || 'Costura',
      tarifa_unitaria: op.tarifa_unitaria ?? '',
      vigente_desde: op.vigente_desde ? op.vigente_desde.split('T')[0] : '',
      notas: op.notas || '',
      activo: op.activo,
    });
    setShowForm(true);
  };

  // Persistir nuevo orden tras un drop. Lo hacemos con un pequeño debounce
  // para que si el usuario hace varios moves rápidos, mande un solo POST.
  const persistirOrden = useCallback((nuevosItems) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        await reordenarOperacionesCostura(nuevosItems.map(i => i.id));
        toast.success('Orden guardado');
      } catch (e) {
        toast.error('Error al guardar el orden');
        cargar(); // recargar desde server para no quedar desincronizado
      }
    }, 250);
  }, [cargar]);

  const handleDragStart = (e, id) => {
    setDragId(id);
    // efecto visual nativo
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(id)); } catch (_) {}
  };
  const handleDragOver = (e, id) => {
    e.preventDefault(); // permitir drop
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };
  const handleDragEnd = () => { setDragId(null); setDragOverId(null); };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (dragId == null || dragId === targetId) { handleDragEnd(); return; }
    setItems(prev => {
      const fromIdx = prev.findIndex(i => i.id === dragId);
      const toIdx = prev.findIndex(i => i.id === targetId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = prev.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      persistirOrden(next);
      return next;
    });
    handleDragEnd();
  };

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const cancel = () => {
    setEditingId(null);
    setShowForm(false);
    setForm(emptyForm());
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    const tarifa = parseFloat(form.tarifa_unitaria);
    if (isNaN(tarifa) || tarifa < 0) { toast.error('Tarifa inválida'); return; }
    setSaving(true);
    try {
      // Orden: al crear, lo dejamos al final (= cantidad actual). Al editar,
      // mantenemos el orden existente (lo maneja el drag & drop, no el form).
      const ordenFinal = editingId
        ? (items.find(i => i.id === editingId)?.orden ?? 0)
        : items.length;
      const payload = {
        ...form,
        nombre: form.nombre.trim(),
        tarifa_unitaria: tarifa,
        vigente_desde: form.vigente_desde || null,
        notas: form.notas.trim() || null,
        orden: ordenFinal,
      };
      if (editingId) {
        await updateOperacionCostura(editingId, payload);
        toast.success('Operación actualizada');
      } else {
        await createOperacionCostura(payload);
        toast.success('Operación creada');
      }
      cancel();
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (op) => {
    if (!window.confirm(`¿Eliminar la operación "${op.nombre}"?\n\nSi tiene avances registrados, se desactiva en lugar de borrarse.`)) return;
    try {
      const r = await deleteOperacionCostura(op.id);
      toast.success(r.data?.estado === 'desactivada'
        ? `Desactivada (tiene ${r.data.avances_existentes} avances asociados)`
        : 'Eliminada');
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scissors className="h-6 w-6" />
            Tarifas de Costura
          </h1>
          <p className="text-sm text-gray-500">
            Catálogo de sub-operaciones de costura con tarifa unitaria. Cambiar tarifa
            no afecta avances ya registrados (cada uno guarda snapshot).
          </p>
        </div>
        <div className="flex gap-2">
          <label className="text-xs flex items-center gap-1 cursor-pointer text-gray-600">
            <input
              type="checkbox"
              checked={incluirInactivas}
              onChange={(e) => setIncluirInactivas(e.target.checked)}
            />
            Ver inactivas
          </label>
          <button
            onClick={startNew}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 flex items-center gap-1"
            data-testid="btn-nueva-operacion"
          >
            <Plus className="h-4 w-4" /> Nueva operación
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={save} className="rounded-lg border bg-white p-4 space-y-3" data-testid="form-operacion">
          <h2 className="text-sm font-semibold flex items-center gap-1">
            {editingId ? <><Edit2 className="h-4 w-4" /> Editar operación</> : <><Plus className="h-4 w-4" /> Nueva operación</>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Nombre *</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm(p => ({ ...p, nombre: e.target.value }))}
                placeholder="ej: Cintura"
                className="border rounded px-2 py-1.5 text-sm w-full"
                autoFocus
                required
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Tarifa unitaria (S/) *</label>
              <input
                type="number" step="0.0001" min="0"
                value={form.tarifa_unitaria}
                onChange={(e) => setForm(p => ({ ...p, tarifa_unitaria: e.target.value }))}
                placeholder="0.80"
                className="border rounded px-2 py-1.5 text-sm w-full font-mono"
                required
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Vigente desde</label>
              <input
                type="date"
                value={form.vigente_desde}
                onChange={(e) => setForm(p => ({ ...p, vigente_desde: e.target.value }))}
                className="border rounded px-2 py-1.5 text-sm w-full"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-0.5">Servicio asociado</label>
              <select
                value={form.servicio_asociado}
                onChange={(e) => setForm(p => ({ ...p, servicio_asociado: e.target.value }))}
                className="border rounded px-2 py-1.5 text-sm w-full"
              >
                <option value="Costura">Costura</option>
                <option value="Atraque">Atraque</option>
                <option value="Acabado">Acabado</option>
                <option value="Bordado">Bordado</option>
              </select>
            </div>
            <label className="flex items-end gap-1.5 text-sm cursor-pointer pb-1.5">
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => setForm(p => ({ ...p, activo: e.target.checked }))}
              />
              Activo
            </label>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-0.5">Notas (opcional)</label>
            <input
              value={form.notas}
              onChange={(e) => setForm(p => ({ ...p, notas: e.target.value }))}
              className="border rounded px-2 py-1.5 text-sm w-full"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50" disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 flex items-center gap-1" disabled={saving}>
              <Check className="h-4 w-4" /> {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      )}

      <div className="rounded-lg border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-center px-2 py-2 w-10" title="Arrastrá para reordenar"></th>
              <th className="text-left px-3 py-2">Nombre</th>
              <th className="text-left px-3 py-2">Servicio</th>
              <th className="text-right px-3 py-2">Tarifa</th>
              <th className="text-left px-3 py-2">Vigente desde</th>
              <th className="text-left px-3 py-2">Notas</th>
              <th className="text-center px-3 py-2 w-20">Estado</th>
              <th className="text-center px-3 py-2 w-24">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400">Sin operaciones registradas. Crea la primera con "Nueva operación".</td></tr>
            ) : items.map(op => (
              <tr
                key={op.id}
                draggable
                onDragStart={(e) => handleDragStart(e, op.id)}
                onDragOver={(e) => handleDragOver(e, op.id)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, op.id)}
                className={`border-t hover:bg-gray-50 transition-colors
                  ${!op.activo ? 'opacity-50' : ''}
                  ${dragId === op.id ? 'opacity-40' : ''}
                  ${dragOverId === op.id && dragId !== op.id ? 'bg-emerald-50 border-t-2 border-t-emerald-500' : ''}`}
                data-testid={`row-op-${op.id}`}
              >
                <td className="px-2 py-2 text-center text-gray-400 cursor-grab active:cursor-grabbing select-none" title="Arrastrá para reordenar">
                  <GripVertical className="h-4 w-4 mx-auto" />
                </td>
                <td className="px-3 py-2 font-medium">{op.nombre}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{op.servicio_asociado}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(op.tarifa_unitaria)}</td>
                <td className="px-3 py-2 text-xs">{op.vigente_desde ? op.vigente_desde.split('T')[0] : '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-500 truncate max-w-[200px]" title={op.notas || ''}>{op.notas || '—'}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${op.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {op.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <button
                      onClick={() => startEdit(op)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      title="Editar"
                      data-testid={`btn-edit-${op.id}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(op)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Eliminar / Desactivar"
                      data-testid={`btn-del-${op.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
